import { useMemo, useSyncExternalStore } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "../lib/api";
import { getJobExpiry } from "../features/jobs/lib/jobExpiry";
import { useRealtimeRecoveryInterval } from "../features/realtime/realtimeEvents";

/* Gemeinsamer kompakter Job-Überblick für Dashboard und Navigations-Badges.
   Jede Quelle besitzt einen eigenen Cache und lädt höchstens eine schlanke
   30er-Seite. SSE ist der primäre Aktualisierungspfad; ein langsamer
   REST-Abgleich läuft nur bei Stream-Ausfall und einem echten Aktivjob. */

export const JOBS_OVERVIEW_LIMIT = 30;
const ACTIVE_STATUSES = new Set([
  "uploading",
  "pending",
  "processing",
  "running",
  "queued",
  "cancelling",
]);

type JobSource = "mockup" | "template" | "design";
type AnyJob = Record<string, any>;

interface JobsOverviewPage {
  jobs: AnyJob[];
  total: number;
}

const SOURCE_URL: Record<JobSource, string> = {
  mockup: "/api/mockup-generation/jobs",
  template: "/api/template-batch/jobs",
  design: "/api/design-generator/jobs",
};

export function buildJobsOverviewRequest(source: JobSource) {
  return {
    url: SOURCE_URL[source],
    params:
      source === "design"
        ? { limit: JOBS_OVERVIEW_LIMIT, sort: "attention" }
        : { limit: JOBS_OVERVIEW_LIMIT, page: 1, view: "summary", sort: "newest" },
  };
}

function extractPage(data: any): JobsOverviewPage {
  const jobs = Array.isArray(data)
    ? data
    : Array.isArray(data?.data)
      ? data.data
      : Array.isArray(data?.jobs)
        ? data.jobs
        : [];
  const rawTotal = data?.pagination?.total;
  return {
    jobs,
    total: Number.isFinite(Number(rawTotal)) ? Number(rawTotal) : jobs.length,
  };
}

function pollingAvailable() {
  const visible = typeof document === "undefined" || document.visibilityState === "visible";
  const online = typeof navigator === "undefined" || navigator.onLine !== false;
  return visible && online;
}

function subscribePollingAvailability(notify: () => void) {
  if (typeof window === "undefined" || typeof document === "undefined") return () => undefined;
  document.addEventListener("visibilitychange", notify);
  window.addEventListener("online", notify);
  window.addEventListener("offline", notify);
  return () => {
    document.removeEventListener("visibilitychange", notify);
    window.removeEventListener("online", notify);
    window.removeEventListener("offline", notify);
  };
}

export function shouldPollJobs(page: JobsOverviewPage | undefined, available: boolean) {
  return available && Boolean(page?.jobs.some(isJobActive));
}

export function jobExpiresAt(job: AnyJob): number | null {
  return getJobExpiry(job)?.expiresAt ?? null;
}

export function isJobActive(job: AnyJob): boolean {
  return ACTIVE_STATUSES.has(String(job.status || "").toLowerCase());
}

export function pendingReviewCount(job: AnyJob): number {
  let count = 0;
  for (const reference of job.references || []) {
    for (const result of reference.results || []) {
      if (result.reviewStatus === "pending_review") count += 1;
    }
  }
  return count;
}

export function reviewedCount(job: AnyJob): { done: number; total: number } {
  let done = 0;
  let total = 0;
  for (const reference of job.references || []) {
    for (const result of reference.results || []) {
      total += 1;
      if (result.reviewStatus && result.reviewStatus !== "pending_review") done += 1;
    }
  }
  return { done, total };
}

async function loadOverviewPage(source: JobSource, signal: AbortSignal) {
  const request = buildJobsOverviewRequest(source);
  const response = await api.get(request.url, { params: request.params, signal });
  return extractPage(response.data);
}

export default function useJobsOverview({ isAdmin }: { isAdmin: boolean }) {
  const canPoll = useSyncExternalStore(subscribePollingAvailability, pollingAvailable, () => false);
  const recoveryInterval = useRealtimeRecoveryInterval();

  const queryOptions = (source: JobSource) => ({
    queryKey: ["jobs-overview", source],
    queryFn: ({ signal }: { signal: AbortSignal }) => loadOverviewPage(source, signal),
    staleTime: 15_000,
    refetchInterval: ({ state }: { state: { data?: JobsOverviewPage } }) =>
      shouldPollJobs(state.data, canPoll) ? recoveryInterval : false,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: ({ state }: { state: { data?: JobsOverviewPage } }) =>
      shouldPollJobs(state.data, canPoll),
    refetchOnReconnect: ({ state }: { state: { data?: JobsOverviewPage } }) =>
      shouldPollJobs(state.data, canPoll),
  });

  const mockupQuery = useQuery(queryOptions("mockup"));
  const templateQuery = useQuery(queryOptions("template"));
  const designQuery = useQuery({ ...queryOptions("design"), enabled: isAdmin });

  const mockupJobs = useMemo<AnyJob[]>(
    () => (mockupQuery.data?.jobs || []).map((job) => ({ ...job, _type: "mockup" })),
    [mockupQuery.data],
  );
  const templateJobs = useMemo<AnyJob[]>(
    () => (templateQuery.data?.jobs || []).map((job) => ({ ...job, _type: "template" })),
    [templateQuery.data],
  );
  const designJobs = useMemo<AnyJob[]>(
    () => ((isAdmin && designQuery.data?.jobs) || []).map((job) => ({ ...job, _type: "design" })),
    [isAdmin, designQuery.data],
  );

  const derived = useMemo(() => {
    const lane = [...mockupJobs, ...templateJobs];
    const now = Date.now();
    const activeLaneJobs = lane.filter(isJobActive);
    const waitingLaneJobs = lane.filter((job) =>
      ["pending", "uploading", "queued"].includes(String(job.status || "").toLowerCase()),
    );
    const readyDownloads = lane.filter((job) => {
      if (String(job.status || "").toLowerCase() !== "completed") return false;
      const expiresAt = jobExpiresAt(job);
      return expiresAt !== null && expiresAt > now;
    });
    const runningDesignJobs = designJobs.filter(isJobActive);
    const reviewJobs = designJobs.filter((job) => pendingReviewCount(job) > 0);
    const reviewPendingTotal = reviewJobs.reduce((sum, job) => sum + pendingReviewCount(job), 0);

    return {
      activeLaneJobs,
      waitingLaneJobs,
      readyDownloads,
      runningDesignJobs,
      reviewJobs,
      reviewPendingTotal,
    };
  }, [mockupJobs, templateJobs, designJobs]);

  return {
    mockupJobs,
    templateJobs,
    designJobs,
    sourceTotals: {
      mockup: mockupQuery.data?.total || 0,
      template: templateQuery.data?.total || 0,
      design: isAdmin ? designQuery.data?.total || 0 : 0,
    },
    ...derived,
    isLoading:
      mockupQuery.isLoading || templateQuery.isLoading || (isAdmin && designQuery.isLoading),
    error: mockupQuery.error || templateQuery.error || (isAdmin ? designQuery.error : null) || null,
    refetch: () =>
      Promise.all([
        mockupQuery.refetch(),
        templateQuery.refetch(),
        ...(isAdmin ? [designQuery.refetch()] : []),
      ]),
  };
}
